// Контекст состояния лицензии клуба для бекофиса.
//
// При монтировании внутри защищённой области (если это НЕ супер-админ) тянет
// GET /v1/license/me и хранит срок/остаток дней/флаг блокировки. Дополнительно
// слушает «живой» 403 license_expired от клиента — даже если /license/me ответил
// «ок», реальный 403 от любого клубного запроса мгновенно включает локаут.
//
// Для супер-админа провайдер пассивен: blocked=false, предупреждений нет.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react'
import { useAuth } from '@/auth/AuthContext'
import { api, setLicenseBlockedHandler, type LicenseBlockedPayload } from '@/api/client'

/** Ответ GET /v1/license/me. */
interface LicenseMe {
  clubId: string | null
  expiresAt: number | null
  blockAtMs: number | null
  daysLeft: number | null
  blocked: boolean
  renewalStatus: string
  contacts: string
}

interface LicenseContextValue {
  /** идёт первичная загрузка статуса */
  loading: boolean
  /** epoch-ms окончания действия лицензии (null — не задана) */
  expiresAt: number | null
  /** epoch-ms момента блокировки доступа */
  blockAtMs: number | null
  /** осталось целых дней (null — не задана/супер-админ) */
  daysLeft: number | null
  /** доступ заблокирован (из /license/me ИЛИ из «живого» 403) */
  blocked: boolean
  /** статус заявки на продление */
  renewalStatus: string
  /** контакты поддержки для экрана блокировки/напоминаний */
  contacts: string
}

const LicenseContext = createContext<LicenseContextValue | null>(null)

/** Дефолтное состояние «лицензия в порядке / неактивно» (супер-админ, ошибка загрузки). */
const INACTIVE: LicenseContextValue = {
  loading: false,
  expiresAt: null,
  blockAtMs: null,
  daysLeft: null,
  blocked: false,
  renewalStatus: 'none',
  contacts: ''
}

export function LicenseProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { claims, isSuperAdmin } = useAuth()

  // Провайдер активен только для клубного сотрудника с привязкой к клубу.
  const active = !isSuperAdmin && claims?.role !== 'super_admin' && Boolean(claims?.club_id)

  const [state, setState] = useState<LicenseContextValue>(() => ({
    ...INACTIVE,
    loading: active
  }))

  // Перезагрузка статуса (mount/login): тянем /license/me, если активны.
  const reload = useCallback(() => {
    if (!active) {
      setState(INACTIVE)
      return
    }
    let alive = true
    setState((s) => ({ ...s, loading: true }))
    api
      .get<LicenseMe>('/v1/license/me')
      .then((data) => {
        if (!alive) return
        setState({
          loading: false,
          expiresAt: data.expiresAt,
          blockAtMs: data.blockAtMs,
          daysLeft: data.daysLeft,
          blocked: data.blocked,
          renewalStatus: data.renewalStatus,
          contacts: data.contacts
        })
      })
      .catch(() => {
        // Сетевая ошибка/прочее: не блокируем UI силой, локаут включит только
        // явный 403 license_expired (через setLicenseBlockedHandler).
        if (alive) setState((s) => ({ ...s, loading: false }))
      })
    return () => {
      alive = false
    }
  }, [active])

  useEffect(() => {
    const cleanup = reload()
    return cleanup
  }, [reload])

  // «Живой» 403 license_expired от любого клубного запроса → мгновенный локаут.
  useEffect(() => {
    if (!active) return
    const handler = (payload: LicenseBlockedPayload): void => {
      setState((s) => ({
        ...s,
        blocked: true,
        contacts: payload.contacts || s.contacts,
        expiresAt: payload.expiresAt ?? s.expiresAt
      }))
    }
    setLicenseBlockedHandler(handler)
    return () => setLicenseBlockedHandler(null)
  }, [active])

  const value = useMemo<LicenseContextValue>(() => state, [state])

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLicense(): LicenseContextValue {
  const ctx = useContext(LicenseContext)
  if (!ctx) throw new Error('useLicense должен вызываться внутри <LicenseProvider>')
  return ctx
}
